/**
 * KpiCard — hero metric card used across the Insights overview and tabs.
 *
 * Anatomy:
 *   - Top: tiny uppercase label + icon
 *   - Middle: large current value + delta pill (vs prior period)
 *   - Right: tiny sparkline (14-day trend)
 *   - Bottom: optional secondary text (subtitle/footnote)
 *
 * Click → navigates to drillDownUrl if provided.
 */

import { Link } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Sparkline } from './sparkline';

export interface KpiCardProps {
  label: string;
  icon?: LucideIcon;
  value: string | number;
  /** Numeric value for delta computation (compares to prevValue) */
  numericValue?: number;
  prevValue?: number;
  /** Direction in which "increase" is good. Defaults to 'up'. */
  direction?: 'up' | 'down';
  /** Override delta with a specific string (e.g., "+12% vs last week") */
  deltaOverride?: string;
  sparkline?: number[];
  sparklineColor?: string;
  /** Subtitle text below the main value */
  subtitle?: string;
  /** Tiny status dot color (e.g., for SLO health) */
  statusDot?: 'emerald' | 'amber' | 'red' | 'muted';
  /** Click navigates here */
  drillDownUrl?: string;
  className?: string;
}

function formatDelta(current: number, prev: number): { pct: number; sign: '+' | '-' | '' } {
  if (prev === 0 && current === 0) return { pct: 0, sign: '' };
  if (prev === 0) return { pct: 100, sign: '+' };
  const diff = ((current - prev) / Math.abs(prev)) * 100;
  return {
    pct: Math.abs(Math.round(diff * 10) / 10),
    sign: diff > 0 ? '+' : diff < 0 ? '-' : '',
  };
}

export function KpiCard({
  label, icon: Icon, value, numericValue, prevValue, direction = 'up',
  deltaOverride, sparkline, sparklineColor, subtitle, statusDot, drillDownUrl, className,
}: KpiCardProps) {
  const delta = numericValue !== undefined && prevValue !== undefined
    ? formatDelta(numericValue, prevValue)
    : null;

  // Color the delta based on direction-of-good
  const isImprovement = delta && (
    (direction === 'up' && delta.sign === '+') ||
    (direction === 'down' && delta.sign === '-')
  );
  const isDegradation = delta && delta.sign !== '' && !isImprovement;

  const deltaColorClass = isImprovement
    ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/20'
    : isDegradation
      ? 'text-red-700 dark:text-red-300 bg-red-500/10 border-red-500/20'
      : 'text-muted-foreground bg-muted/40 border-border';

  const dotColor = {
    emerald: 'bg-emerald-500',
    amber:   'bg-amber-500',
    red:     'bg-red-500',
    muted:   'bg-muted-foreground/40',
  }[statusDot ?? 'muted'];

  const cardInner = (
    <CardContent className="p-4 h-full">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
          <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground truncate">
            {label}
          </span>
          {statusDot && (
            <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', dotColor)} />
          )}
        </div>
        {sparkline && sparkline.length > 1 && (
          <Sparkline
            values={sparkline}
            color={sparklineColor ?? (isDegradation ? 'rgb(248 113 113)' : 'rgb(52 211 153)')}
            width={64}
            height={20}
            className="shrink-0"
          />
        )}
      </div>

      <div className="flex items-baseline gap-2 flex-wrap">
        <div className="text-2xl font-bold tabular-nums leading-none">{value}</div>
        {(deltaOverride || delta) && (
          <span className={cn(
            'text-[10px] font-semibold px-1.5 py-0.5 rounded border tabular-nums',
            deltaColorClass,
          )}>
            {deltaOverride ?? `${delta!.sign}${delta!.pct}%`}
          </span>
        )}
      </div>

      {subtitle && (
        <p className="text-[11px] text-muted-foreground mt-1.5 truncate" title={subtitle}>
          {subtitle}
        </p>
      )}
    </CardContent>
  );

  if (drillDownUrl) {
    return (
      <Card className={cn('transition-colors hover:bg-muted/20 cursor-pointer', className)}>
        <Link to={drillDownUrl as any} className="block h-full">
          {cardInner}
        </Link>
      </Card>
    );
  }

  return <Card className={className}>{cardInner}</Card>;
}
